import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import isEqual from "lodash/isEqual";
import {
  useListContext,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useState, type CSSProperties } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { useReadOnly } from "../misc/fromBitrix";
import { RelativeDate } from "../misc/RelativeDate";
import type { Lead, PipelineStage } from "../types";
import { leadRepresentation } from "./sources";

type LeadsByStage = Record<string, Lead[]>;

/** leads by stage id; a lead on a stage the board does not have goes first */
const groupLeadsByStage = (
  leads: Lead[],
  stages: PipelineStage[],
): LeadsByStage => {
  if (!stages.length) return {};
  const out: LeadsByStage = Object.fromEntries(
    stages.map((s) => [String(s.id), [] as Lead[]]),
  );
  for (const lead of leads) {
    (out[String(lead.stage_id)] ?? out[String(stages[0].id)]).push(lead);
  }
  for (const column of Object.values(out)) {
    column.sort((a, b) =>
      (b.created_at ?? "").localeCompare(a.created_at ?? ""),
    );
  }
  return out;
};

/**
 * The lead board of one project: a column per stage of its lead pipeline.
 * Dragging a card moves the lead between open and lost stages. The «won»
 * column takes no drops: a lead gets there only through «В сделку», which
 * makes its deal (the database refuses anything else). Newest leads first.
 */
export const LeadBoard = ({
  stages,
  leads,
}: {
  stages: PipelineStage[];
  leads: Lead[];
}) => {
  const { refetch } = useListContext<Lead>();
  const [byStage, setByStage] = useState(() =>
    groupLeadsByStage(leads, stages),
  );
  useEffect(() => {
    const next = groupLeadsByStage(leads, stages);
    setByStage((current) => (isEqual(current, next) ? current : next));
  }, [leads, stages]);
  const [update] = useUpdate<Lead>();
  const notify = useNotify();

  const onDragEnd: OnDragEndResponder = ({ source, destination }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    const lead = byStage[source.droppableId]?.[source.index];
    const target = stages.find((s) => String(s.id) === destination.droppableId);
    if (!lead || !target || target.outcome === "won") return;

    // move the card at once, then save
    const from = byStage[source.droppableId].filter((l) => l.id !== lead.id);
    const moved = { ...lead, stage_id: target.id };
    const to = [moved, ...byStage[destination.droppableId]];
    setByStage({
      ...byStage,
      [source.droppableId]: from,
      [destination.droppableId]: to,
    });

    update(
      "leads",
      { id: lead.id, data: { stage_id: target.id }, previousData: lead },
      {
        mutationMode: "pessimistic",
        onSuccess: () => refetch(),
        onError: (error) => {
          notify((error as Error)?.message || "ra.notification.http_error", {
            type: "error",
          });
          refetch();
        },
      },
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {/* relative: keeps the columns' screen-reader texts inside the
          scroller, so a phone page does not get wider than the screen */}
      <div className="relative flex gap-3 overflow-x-auto -mx-4 px-4">
        {stages.map((stage) => (
          <LeadColumn
            key={stage.id}
            stage={stage}
            leads={byStage[String(stage.id)] ?? []}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

const LeadColumn = ({
  stage,
  leads,
}: {
  stage: PipelineStage;
  leads: Lead[];
}) => {
  const translate = useTranslate();
  return (
    // min-w: on a phone the board scrolls sideways inside itself
    <div className="flex-1 min-w-40 pb-8" data-stage={stage.id}>
      <div className="flex items-baseline justify-between gap-2 px-0.5">
        <h3 className="vv-heading leading-tight truncate">{stage.name}</h3>
        <span
          className="text-xs text-muted-foreground tabular-nums"
          aria-label={translate("crm.leads.column_count", {
            smart_count: leads.length,
          })}
        >
          {leads.length}
        </span>
      </div>
      {stage.outcome === "won" ? (
        <p className="px-0.5 text-[0.6875rem] text-muted-foreground">
          {translate("crm.leads.won_hint")}
        </p>
      ) : null}
      <Droppable
        droppableId={String(stage.id)}
        isDropDisabled={stage.outcome === "won"}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "flex flex-col rounded-xl mt-3 gap-2 min-h-16 transition-colors",
              snapshot.isDraggingOver && "bg-muted/70",
            )}
          >
            {leads.map((lead, index) => (
              <LeadCard key={lead.id} lead={lead} index={index} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

const LeadCard = ({ lead, index }: { lead: Lead; index: number }) => {
  const translate = useTranslate();
  const redirect = useRedirect();
  // a Bitrix24 lead in Bitrix24 mode moves between stages in Bitrix24
  const readOnly = useReadOnly(lead);
  return (
    <Draggable
      draggableId={String(lead.id)}
      index={index}
      isDragDisabled={readOnly}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="cursor-pointer"
          onClick={() =>
            redirect(
              `/leads/${lead.id}/show`,
              undefined,
              undefined,
              undefined,
              {
                _scrollToTop: false,
              },
            )
          }
        >
          <Card
            className={cn(
              "py-3 gap-0 vv-rise",
              snapshot.isDragging
                ? "opacity-90 rotate-1 shadow-lg border-primary/60"
                : "shadow-none vv-card-hover",
            )}
            style={{ "--vv-i": index } as CSSProperties}
          >
            <CardContent className="px-3 flex flex-col gap-1 min-w-0">
              <p className="text-sm font-medium leading-snug line-clamp-2 break-words">
                {leadRepresentation(lead)}
              </p>
              {lead.phone && lead.phone !== leadRepresentation(lead) ? (
                <p className="text-xs text-muted-foreground truncate tabular-nums">
                  {lead.phone}
                </p>
              ) : null}
              {lead.company_name &&
              lead.company_name !== leadRepresentation(lead) ? (
                <p className="text-xs text-muted-foreground truncate">
                  {lead.company_name}
                </p>
              ) : null}
              <p className="text-[0.6875rem] text-muted-foreground truncate">
                {translate(`crm.leads.sources.${lead.source}`)} ·{" "}
                <RelativeDate date={lead.created_at} />
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
  );
};
